export const reportsArticles = [
    {
        id: 'reports.overview',
        title: 'Reports Overview',
        category: 'Reports',
        order: 1,
        summary: 'Generating and viewing system reports',
        content: `
The Reports page provides access to various reports for estimating, financial tracking, and project management.

**Report Categories:**

- **Estimating Reports** - BOQ summaries, price lists
- **Financial Reports** - Invoices, payments, budgets
- **Project Reports** - Job status, progress tracking

**Features:**

- Filter by date range and job
- Preview before printing
- Export to PDF or Excel
    `,
        expandableSections: [
            {
                title: 'Report List',
                content: `
Available reports:

**Job costing:** job list (summary and detailed), cost centres (budget from the BOQ engine, ordered, invoiced, forecast and variance on a GST-inclusive basis), budgets, looking-for-trouble, unpaid invoices

**Debtors:** aged debtors (summary and detail), client statement, contract summary

**Orders:** order profile, order statuses, over-runs

**Procurement:** unmatched BOQ items, order reconciliation (every ordered purchase order against its BOQ lines — orders whose lines are not flagged as ordered, orders with only the Databuild snapshot, no lines, or no supplier; an owner, admin, manager or accounts member can flag the lines from the report, which is what Send does)

**Financial:** retention ledger, aged payables, supplier compliance, cash-flow forecast (up to 60 periods), and the ledger / accounting-sync set: **Posting Reconciliation** (every document since the cut-over — approved, in the ledger, in the accounting platform, failed), **GST / BAS Summary** (G1, 1A, G11, 1B per month or quarter with the ledger GST accounts as a cross-check), **Job Cash Position** (bills, paid, claimed, received, receivable, net cash per job), **Supplier Statement Reconciliation** (Costed invoices vs what reached the platform, per supplier), **Retention Schedule** (held retentions by release month), **Client Receipts Ageing** (claims vs receipts per client, aged), **Job Profit & Loss (Ledger)** (revenue, direct cost, gross and net per job from posted journals), **Overheads by Month** (the no-job overhead account by supplier) and **Trial Balance**. All of these take their parameters from the bar above the report and export to Excel and custom layouts.

**Project:** work in progress, profitability

**Estimating:** price movement, recipe margin

Every role can open and print these. Custom layouts, the Report Designer and query blocks are described in their own articles.
        `,
            },
        ],
        keywords: ['reports', 'printing', 'export', 'summary'],
        route: '/reports',
        relatedArticles: ['reports.estimating', 'reports.financial'],
    },
    {
        id: 'reports.estimating',
        title: 'Estimating Reports',
        category: 'Reports',
        order: 2,
        summary: 'BOQ and pricing reports',
        content: `
Estimating reports provide summaries and details of your bills of quantities.

**Available Reports:**

- BOQ Summary - Cost centre totals
- BOQ Detail - Full line item listing
- Price Comparison - Compare prices across jobs
    `,
        expandableSections: [
            {
                title: 'BOQ Summary Report',
                content: `
Shows cost centre totals for a job:

1. Select **BOQ Summary** report
2. Choose the Job
3. Select Price Level
4. Set Bill Date
5. Click **Generate**

The report shows:
- Cost centre hierarchy
- Subtotals per section
- Bill total
        `,
            },
            {
                title: 'BOQ Detail Report',
                content: `
Shows all line items:

1. Select **BOQ Detail** report
2. Choose the Job
3. Set filters (cost centre, supplier)
4. Click **Generate**

Includes:
- Every line item
- Quantities and prices
- Extended values
- Cost centre organization
        `,
            },
        ],
        keywords: ['estimating reports', 'BOQ report', 'price list'],
        route: '/reports',
        relatedArticles: ['reports.overview', 'boq.overview'],
    },
    {
        id: 'reports.financial',
        title: 'Financial Reports',
        category: 'Reports',
        order: 3,
        summary: 'Invoice, payment, and budget reports',
        content: `
Financial reports help track payables, receivables, and budget performance.

**Available Reports:**

- Aged Payables - What you owe suppliers
- Aged Receivables - What clients owe you
- Budget vs Actual - Variance analysis

**Where the budget comes from.** Job Costing, Over-runs and (for workspaces set up in Costed) Profitability take each job's budget from the BOQ engine — the same figure the BOQ screen shows — never from a raw line total. Workspaces migrated from Databuild show committed cost (the greater of ordered or invoiced per supplier) as the Profitability budget.
- Payment History - Past payments

**Sales reports** (Reports → Sales): the **Tender Register** lists every lead with a closing date — days left, bid / no bid, probability, weighted value, owner and estimator, with a status of Open, Due this week, Overdue, No bid, Won or Lost; **Win / Loss** counts won and lost leads for a period grouped by source, owner, month closed, tender type or suburb, with the win rate, values, average days to close and the most common lost reason, next to what is still open in each group. Both come from the Sales board (see the Sales Pipeline help).
    `,
        expandableSections: [
            {
                title: 'Aged Payables Report',
                content: `
Shows outstanding supplier invoices by age:

1. Select **Aged Payables**
2. Set "As At" date
3. Choose aging periods (30/60/90 days)
4. Click **Generate**

Report shows:
- Supplier name
- Current (not due)
- 30 days overdue
- 60 days overdue
- 90+ days overdue
- Total owing
        `,
            },
            {
                title: 'Budget vs Actual',
                content: `
Compare budgeted amounts to actual costs:

1. Select **Budget vs Actual**
2. Choose the Job
3. Select grouping (cost centre, supplier)
4. Click **Generate**

Shows:
- Budgeted amount
- Committed (ordered)
- Actual (invoiced)
- Variance
        `,
            },
        ],
        keywords: ['financial reports', 'aged payables', 'receivables', 'budget'],
        route: '/reports',
        relatedArticles: ['reports.overview', 'accounting.overview'],
    },
    {
        id: 'reports.export',
        title: 'Exporting Reports',
        category: 'Reports',
        order: 4,
        summary: 'Exporting reports to PDF and Excel',
        content: `
Export reports to different formats for sharing or further analysis.

**Export Formats:**

- **PDF** - For printing and sharing
- **Excel** - For further analysis
- **Print** - Direct to printer
    `,
        expandableSections: [
            {
                title: 'Exporting',
                content: `
After generating a report:

1. Click **Export** dropdown
2. Choose format:
   - PDF - Downloads PDF file
   - Excel - Downloads XLSX file
   - Print - Opens print dialog
3. File downloads automatically

**Email:**
1. Click **Email**
2. Enter recipient(s)
3. Add message
4. Send

Report attaches as PDF.
        `,
            },
        ],
        keywords: ['export', 'PDF', 'Excel', 'print', 'download'],
        route: '/reports',
        relatedArticles: ['reports.overview'],
    },
];
